/**
 * Privacy Notice — standalone legal page at /privacy.
 *
 * Linked from the game player footer and the consent screen. Reuses the
 * game-player shell styling (nav + prose) but scrolls, since the content is
 * long-form. The actual legal copy lives in the `<section>` below — replace the
 * placeholder with the reviewed text.
 */

import { Link } from "react-router";
import labsLogo from "../assets/labs-logo.svg";

export function PrivacyNotice() {
  return (
    <div className="pp-legal">
      <div className="pp-nav">
        <Link to="/" className="pp-back" aria-label="Back">
          <svg className="pp-back-icon" viewBox="0 0 8 9">
            <path d="M7.13761 0V1.38787L1.68923 4.29049L1.07064 4.44911L1.6813 4.60772L7.13761 7.49449V8.88236L0 5.25804V3.60053L7.13761 0Z" fill="white"/>
          </svg>
        </Link>
        <img src={labsLogo} alt="Iconic Labs" className="pp-nav-logo" />
      </div>

      <main className="pp-legal-body">
        <h1 className="pp-headline">Privacy Notice</h1>

        <section className="pp-consent-text">
          <p>
            <em>Last updated 13th of July 2026.</em> This notice explains what
            personal data we collect when you play the Pressure Point demo, why
            we collect it, who receives it, where it goes, how long we keep it,
            and how you can exercise your rights.
          </p>

          <h2>Who we are</h2>
          <p>
            The controller of your personal data is Iconic Interactive Ltd,
            Company Number 14755979, Simpsons Farm, C/O Affect Group, Pentlow,
            Sudbury, England, CO10 7JT.
          </p>
          <p>
            Contact for privacy matters:{" "}
            <a href="mailto:privacy@iconicgames.io">privacy@iconicgames.io</a>
          </p>

          <h2>What Pressure Point is and what we process to make it work</h2>
          <p>
            Pressure Point is an AI-enabled demo where you speak to an AI
            character (virtual agent) using your microphone. We process your
            voice along with a transcript of what you say so the character can
            understand and respond: this is necessary for the primary game
            mechanic to function. The character's responses are generated by AI
            and respond not only to the words you say, but also to your tones
            and inflections. As a player, you can choose to contribute your
            audio recordings for the improvement and training of our models.
            Without your consent only technical logs are retained for purposes
            of debugging and abuse prevention (see below).
          </p>
          <p>
            You must be 18 or over to play. We ask you to confirm this before
            the demo starts.
          </p>

          <h2>The two ways your data is used</h2>
          <p>
            <strong>1. Playing the demo (18+ only).</strong> Your voice and its
            transcript are processed in real time so the character can respond.
            We possess a legitimate interest in operating and demonstrating the
            product and preventing abuse. This processing is transient:
            recordings and transcripts are not kept after your session for this
            purpose. Limited technical logs (which may include transcripts, but
            never audio recordings) are kept for up to 30 days solely to
            prevent abuse and debug technical issues.
          </p>
          <p>
            <strong>
              2. Contributing to model development (optional, opt-in only).
            </strong>{" "}
            If and only if you choose "Agree and contribute", we keep your
            voice recording and transcript in order to use them for commercial
            model development. We will use them to develop and improve Iconic's
            AI models for interactive characters, including the speech
            recognition, dialogue and performance capabilities of our AI
            characters and the underlying foundation models that power them,
            across our current and future games and products. This includes
            improving how our characters respond to the way things are said
            (tone, pacing, energy), not only the words.
          </p>
          <p>
            Users who choose "Agree and contribute," also consent to our usage
            of retained audio and transcripts containing incidental disclosures
            of special category data (health, religion, sexuality, political
            opinions, trade union membership) in our training for the purposes
            listed above. We also acknowledge that players often "role-play,"
            when interacting with virtual agents, and incidental mentions of
            special category data may be fictious. Recordings are not routinely
            listened to by humans, though limited review may occur for
            technical purposes. Models will not directly replay a person's
            incidental mentions of special category data for purposes of
            Article 9 of the UK GDPR.
          </p>
          <p>
            Our legal basis for this is your consent, which users may withdraw
            at any time (see below). We may publish research about the results
            of this work. Published research will not include your recordings
            or transcripts. This consent is entirely optional, you can play the
            full demo should you decline. You must be over 18 to consent to
            these terms.
          </p>
          <p>
            Your data will never be used to sell you ads or to profile
            individual users.
          </p>

          <h2>What we do not collect</h2>
          <p>
            We do not ask you to create an account, log in, or give us your
            name or email address. Retained recordings and transcripts (when
            opted in) are stored without login or email details. We do not use
            your voice to identify who you are, we do not store any assessment
            of your emotional state, and we do not build a profile of you.
          </p>

          <h2>Who receives your data and where it goes</h2>
          <ul>
            <li>
              <strong>Website hosting:</strong> When you visit our website, it
              is served by Railway Corporation (US), our hosting provider. This
              necessarily makes your IP address and standard connection
              information (such as browser type) visible to them, which they
              process on our behalf in accordance with their privacy policy:{" "}
              <a
                href="https://railway.com/legal/privacy"
                target="_blank"
                rel="noreferrer"
              >
                https://railway.com/legal/privacy
              </a>
              . We do not use this information to identify or profile you.
              Transfers to Railway in the US are protected by the European
              Commission's Standard Contractual Clauses, together with the UK
              International Data Transfer Addendum for data originating in the
              UK.
            </li>
            <li>
              <strong>Bot and abuse prevention:</strong> Before you play, we
              use Cloudflare Turnstile to check that you are a human and not an
              automated program. Turnstile processes limited technical
              information about your device and browser, including your IP
              address, to make this assessment. It does not show you puzzles
              (eg: Captcha), track you across websites, or build an advertising
              profile. Cloudflare processes this data in accordance with its
              privacy policy:{" "}
              <a
                href="https://www.cloudflare.com/turnstile-privacy-policy/"
                target="_blank"
                rel="noreferrer"
              >
                https://www.cloudflare.com/turnstile-privacy-policy/
              </a>
              . We possess a legitimate interest in protecting the demo from
              abuse and automated misuse. Transfers to Cloudflare in the US are
              protected by the European Commission's Standard Contractual
              Clauses, together with the UK International Data Transfer
              Addendum for data originating in the UK.
            </li>
            <li>
              <strong>Game connection:</strong> In order to play the game, your
              browser will connect to Amazon Web Services to send your inputs
              to the game server and stream the graphics back to your computer.
              This may make your IP address visible to them, which they process
              in accordance with their privacy policy:{" "}
              <a
                href="https://aws.amazon.com/privacy"
                target="_blank"
                rel="noreferrer"
              >
                https://aws.amazon.com/privacy
              </a>
            </li>
            <li>
              <strong>Your voice recordings:</strong> Processed in the region
              closest to you (EU, US or Japan) by Amazon Web Services. Your
              voice data is never stored or retained within Amazon Web
              Services.
            </li>
            <li>
              <strong>
                Technical logs (which may include transcripts of your
                interactions):
              </strong>{" "}
              Processed and stored in the United States, with technical logs
              kept up to 30 days. Transfers to Modal in the US are protected by
              the European Commission's Standard Contractual Clauses, together
              with the UK International Data Transfer Addendum for data
              originating in the UK. Our Processors are Google Cloud Compute
              Engine, AWS Gamelift Streams, Modal, Railway, and Cloudflare. All
              transfers are subject to signed compliant processing agreements,
              which also apply to their sub-processors.
            </li>
            <li>
              <strong>Retained contributions (opt-in only):</strong> stored on
              Google Cloud strictly within the EU. This may include voice
              recordings (only while you are pushing-to-talk) and game
              interaction data.
            </li>
            <li>
              None of these providers may use your data to train or improve
              their own services. Model development is carried out in
              controlled cloud environments.
            </li>
          </ul>

          <h2>How long we keep your data</h2>
          <p>
            <strong>If you do not opt in:</strong> Nothing is retained beyond
            the live session except technical logs, which are retained for up
            to 30 days.
          </p>
          <p>
            <strong>If you opt in:</strong> We keep your recording and
            transcript for as long as they are actively used to develop and
            improve our AI models. Our retention of your data is subject to a
            yearly review, wherein we will retain your data only if our
            research plan still covers the usage and purposes towards which its
            collection was subject to, and only if the data is still relevant
            to the training of our models. These yearly reviews will be
            summarised in an internal report.
          </p>

          <h2>
            Your privacy reference code, withdrawing consent, and deletion
          </h2>
          <p>
            Should you opt in, we will provide you with a randomly generated
            privacy reference code. This code is the only link between you and
            your contribution, and we strongly recommend you download a copy of
            it. You can notify us anytime at{" "}
            <a href="mailto:privacy@iconicgames.io">privacy@iconicgames.io</a>{" "}
            to withdraw this consent and have your recording and transcript
            deleted.
          </p>
          <p>
            Because we do not collect your name or contact details, your
            privacy reference code is the primary link we hold to your
            contribution. If the user has not provided enough information to
            confidently identify them, we may over-delete data (for example,
            all data from a given time period) to make sure that the request is
            enforced. This ensures that requests are correctly handled in as
            many cases as possible. We may refuse a request if we cannot
            identify the records, or where it is possible that sharing records
            could infringe on the privacy of another user.
          </p>
          <p>
            Withdrawing consent stops all future use of your data and leads to
            deletion of your stored recording and transcript. Doing so does not
            undo model development already completed before you withdrew: it
            may not be technically possible to remove an individual
            contribution from an already-trained model. Withdrawn data is
            excluded from all future training.
          </p>

          <h2>Your rights</h2>
          <p>
            Under UK/EU data protection law you have rights of access,
            rectification, erasure, restriction, objection and portability, and
            the right to withdraw consent at any time using your privacy
            reference code (as described above). You may also file a complaint
            with your supervisory authority: in the UK, the Information
            Commissioner's Office (
            <a href="https://ico.org.uk" target="_blank" rel="noreferrer">
              ico.org.uk
            </a>
            ); in the EU, your national data protection authority. To exercise
            rights, contact{" "}
            <a href="mailto:privacy@iconicgames.io">privacy@iconicgames.io</a>{" "}
            and include your privacy reference code where your request concerns
            retained contributions.
          </p>

          <h2>Cookies and analytics</h2>
          <p>
            We do not use any advertising cookies or third-party trackers, and
            do not store cookies on your device. Cloudflare Turnstile, which we
            use to verify you are human, may store a strictly necessary
            short-lived token or cookie in your browser solely for security
            purposes. These strictly necessary items do not require consent
            under UK/EU law and are not used to track you.
          </p>

          <h2>Changes to this notice</h2>
          <p>
            We will post any updates to this notice here with a revised "last
            updated" date.
          </p>
        </section>
      </main>

      <footer className="landing-footer">Copyright Iconic Interactive 2026</footer>
    </div>
  );
}
